import { Select, Tooltip } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    defaultPeriodIndex,
    formatTargetPercent,
    rateAgainstTarget,
    TRAFFIC_LIGHT_LEVELS,
    type SeriesPoint,
    type TrafficLightLevel,
} from '../../domain/trafficLight';

interface TrafficLightProps {
    readonly points: readonly SeriesPoint[];
    /** Saved target for this chart, on the same (displayed-currency) scale as the points. */
    readonly referenceValue?: number;
}

// Matches the legacy Delphi `Lampa` procedure (old/delphi/uNewGrafice.pas), which paints
// each lamp with pure rgb(255,0,0) / rgb(255,255,0) / rgb(0,255,0) / rgb(0,0,255).
const LEVEL_COLORS: Record<TrafficLightLevel, string> = {
    red: '#ff0000',
    yellow: '#ffff00',
    green: '#00ff00',
    blue: '#0000ff',
};

// The lamp dots keep the legacy pure hues, but text painted in them (notably pure yellow
// and pure green) is unreadable on a light card. Percent labels therefore use darkened,
// AA-contrast variants of the same hues.
const LEVEL_TEXT_COLORS: Record<TrafficLightLevel, string> = {
    red: '#cf1322',
    yellow: '#ad6800',
    green: '#389e0d',
    blue: '#0958d9',
};

interface BandStyle {
    readonly background: string;
    readonly border: string;
}

// Discrete low-opacity band tint behind the whole row, so the rating reads as one unit
// against the chart body above it and the percent text always has a contrasting backdrop.
const LEVEL_BANDS: Record<TrafficLightLevel, BandStyle> = {
    red: { background: 'rgba(207, 19, 34, 0.08)', border: 'rgba(207, 19, 34, 0.22)' },
    yellow: { background: 'rgba(173, 104, 0, 0.10)', border: 'rgba(173, 104, 0, 0.24)' },
    green: { background: 'rgba(56, 158, 13, 0.08)', border: 'rgba(56, 158, 13, 0.22)' },
    blue: { background: 'rgba(9, 88, 217, 0.08)', border: 'rgba(9, 88, 217, 0.22)' },
};

/** Neutral band used while the picked period has no rateable value. */
const NEUTRAL_BAND: BandStyle = { background: 'rgba(0, 0, 0, 0.03)', border: '#f0f0f0' };

const LEVEL_LABELS: Record<TrafficLightLevel, { key: string; fallback: string }> = {
    red: { key: 'statistics.charts.trafficLightRed', fallback: 'Critical (≤75% of target)' },
    yellow: { key: 'statistics.charts.trafficLightYellow', fallback: 'Below target (75–90%)' },
    green: { key: 'statistics.charts.trafficLightGreen', fallback: 'On target (90–105%)' },
    blue: { key: 'statistics.charts.trafficLightBlue', fallback: 'Above target (>105%)' },
};

/**
 * Per-chart traffic light: rates the chosen period's actual value against the chart's
 * saved target (reference line) and lights one of four lamps. Renders nothing until a
 * positive target is set, since the rating is undefined without one.
 */
function TrafficLight({ points, referenceValue }: TrafficLightProps): JSX.Element | null {
    const { t } = useTranslation();
    const [picked, setPicked] = useState<number | null>(null);

    if (!referenceValue || referenceValue <= 0 || points.length === 0) {
        return null;
    }

    const lastIndex = points.length - 1;
    // Default to the most recent non-zero period so trailing empty buckets (common in
    // the daily bilant-backed series) don't make the light default to a useless -100%.
    const pickedIsValid = picked !== null && picked >= 0 && picked <= lastIndex;
    const activeIndex = pickedIsValid ? picked : defaultPeriodIndex(points);
    const rating = rateAgainstTarget(points[activeIndex].value, referenceValue);
    const band = rating ? LEVEL_BANDS[rating.level] : NEUTRAL_BAND;

    return (
        <div
            data-testid="traffic-light-band"
            data-level={rating?.level ?? 'none'}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                marginTop: 6,
                fontSize: 12,
                // Stretch the band across the full card body width (the Statistics card body
                // is padded 8px, see Statistics.compressed.css) so it reads as a footer strip.
                // Only the sides are pulled out: some cards render a ChartHint below the
                // wrapper, so the bottom spacing must stay intact.
                marginLeft: -8,
                marginRight: -8,
                padding: '4px 10px',
                backgroundColor: band.background,
                border: `1px solid ${band.border}`,
                borderRadius: 4,
            }}
        >
            <span style={{ color: '#595959' }}>
                {t('statistics.charts.trafficLight', 'Target rating')}
            </span>
            <Select
                size="small"
                value={activeIndex}
                onChange={setPicked}
                style={{ minWidth: 96 }}
                options={points.map((point, index) => ({ label: point.label, value: index }))}
            />
            <span style={{ display: 'inline-flex', gap: 4 }} aria-hidden="true">
                {TRAFFIC_LIGHT_LEVELS.map(level => {
                    const active = rating?.level === level;
                    return (
                        <Tooltip key={level} title={t(LEVEL_LABELS[level].key, LEVEL_LABELS[level].fallback)}>
                            <span
                                data-testid={`traffic-lamp-${level}`}
                                data-active={active}
                                style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    border: '1px solid #999',
                                    backgroundColor: active ? LEVEL_COLORS[level] : '#e8e8e8',
                                    opacity: active ? 1 : 0.35,
                                }}
                            />
                        </Tooltip>
                    );
                })}
            </span>
            {rating && (
                <span style={{ color: LEVEL_TEXT_COLORS[rating.level], fontWeight: 600 }}>
                    {formatTargetPercent(rating.percent)}
                </span>
            )}
        </div>
    );
}

export default TrafficLight;
